export namespace StringUtils {
    /**
     * Formats a number by adding commas for thousands separator and truncating decimal places.
     *
     * @param {number} value - The number to format.
     * @param {boolean} useNbsp - Optional flag to use non-breaking space as a separator. Defaults to true.
     * @return {string} The formatted number with commas and optional non-breaking space separator.
     */
    export const formatNumber = (value: number, useNbsp = true): string => {
        if (typeof value !== "number") {
            return "---";
        }
        const ref = value.toFixed(2).split(".");
        let preComma = ref[0] || "";
        const postComma = ref[1];
        preComma = stringReverse((stringReverse(preComma).match(/.{1,3}/g) || [""]).join(useNbsp ? ";psbn&" : " "));

        return postComma != "00" ? preComma + "," + postComma : preComma;
    };

    /**
     * Formats a currency value by calling formatNumber and appending the currency symbol.
     *
     * @param {number} value - The value to format as currency.
     * @param {boolean} useNbsp - Optional flag to use non-breaking space as a separator. Defaults to true.
     * @return {string} The formatted currency value with the currency symbol.
     */
    export const formatCurrency = (value: number, useNbsp = true): string => {
        // this is also case for 0
        if (!value || isNaN(value)) {
            value = 0;
        }
        return `${formatNumber(value, useNbsp)}${useNbsp ? "&nbsp;" : " "}Kč`;
    };

    /**
     * Limits the length of the text if it exceeds the specified limit by truncating and adding ellipsis.
     *
     * @param {string} text - The input text to limit.
     * @param {number} limit - The maximum length of the text before truncation. Defaults to 50.
     * @return {string} The text with limited length, potentially truncated with ellipsis.
     */
    export const limitTextLength = (text: string, limit = 50) => {
        if (text && text.length > limit) {
            return text.substring(0, limit - 3) + "...";
        }
        return text;
    };

    /**
     * Reverses the given string.
     *
     * @param {string} str - The string to be reversed.
     * @return {string} The reversed string.
     */
    export function stringReverse(str: string): string {
        return str.split("").reverse().join("");
    }

    /**
     * Converts given HTML text to plain text.
     *
     * @param {string} htmlText - The HTML text to be converted to plain text.
     * @return {string} The plain text.
     */
    export function getPlainTextFromHtml(htmlText: string): string {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlText;
        return tempDiv.textContent || tempDiv.innerText || "";
    }

    /**
     * Sanitizes a string by removing HTML tags and replacing multiple whitespaces/newlines with a single space.
     */
    export function sanitizeString(input: string): string {
        return input
            .replace(/<[^>]*>/g, "") // Remove HTML tags
            .replace(/\s+/g, " ") // Replace multiple whitespaces/newlines with a single space
            .trim(); // Remove leading and trailing spaces
    }

    /**
     * Converts a number to its ordinal form based on gender. ("m" for masculine, "f" for feminine, "n" for neutral = default).
     *
     * Supports numbers from 1 to 9.
     */
    export function numberToOrdinal(number: number, gender: "m" | "f" | "n" = "n"): string {
        if (!number) return "";
        if (number < 1 || number > 9) return `${number}.`;

        const ordinals = ["první", "druh", "třetí", "čtvrt", "pát", "šest", "sedm", "osm", "devát"];
        const endings = { m: "ý", f: "á", n: "é" };

        switch (number) {
            case 1:
            case 3:
                return ordinals[number - 1];
            default:
                return ordinals[number - 1] + endings[gender];
        }
    }

    /**
     * Capitalizes the first letter of the given string.
     */
    export function capitalize(str: string): string {
        if (!str) return "";

        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
